/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { Fade, useMediaQuery, useTheme } from '@material-ui/core';

import { Text } from 'components';
import { ChallengePhaseType, ICustomTheme } from 'interfaces';
import { CHALLENGE_PHASE } from 'constants/challenges';
import * as S from './ActionParticipation.styles';

const { cssAlignIcon, cssDisabled } = S;

type ActionParticipationType = {
  currentPhase?: ChallengePhaseType | null;
  hasResultLimit: boolean;
  inscriptionsLeft: number | undefined;
  isParticipating: boolean | undefined;
  half?: boolean;
  withImage?: boolean;
};

const ActionParticipation = ({
  currentPhase,
  hasResultLimit,
  inscriptionsLeft,
  isParticipating,
  half = false,
  withImage = false,
}: ActionParticipationType): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const isAfterChallenge = currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;
  let participation;
  if (isParticipating) {
    // I participate / I participated
    participation = (
      <span css={cssAlignIcon}>
        <S.CheckCircleIcon fontSize="large" disabled={isAfterChallenge} />
        {t(
          isAfterChallenge
            ? 'challenges.i_participated'
            : 'challenges.i_participate',
        )}
      </span>
    );
  } else if (
    isParticipating === false ||
    (isParticipating === undefined &&
      currentPhase !== CHALLENGE_PHASE.DURING_SIGN_IN)
  ) {
    // I do not participate / I did not participate
    participation = (
      <span css={cssAlignIcon}>
        <S.CancelIcon fontSize="large" disabled={isAfterChallenge} />
        <span css={cssDisabled}>
          {t(
            isAfterChallenge
              ? 'challenges.i_didnt_participate'
              : 'challenges.i_dont_participate',
          )}
        </span>
      </span>
    );
  }

  return (
    <React.Fragment>
      {/* Number of remaining places */}
      {currentPhase === CHALLENGE_PHASE.BEFORE_SIGN_IN && hasResultLimit && (
        <S.Title
          color="primary"
          size={isDownSm ? 'h5' : 'h4'}
          isCentered={half && !withImage}
        >
          <S.SeatIcon />
          <strong>
            {t(
              Number(inscriptionsLeft) < 2
                ? 'challenges.nb_available_places'
                : 'challenges.nb_available_places_plural',
              { nb: inscriptionsLeft?.toLocaleString() },
            )}
          </strong>
        </S.Title>
      )}

      {participation && currentPhase !== CHALLENGE_PHASE.BEFORE_SIGN_IN && (
        <S.Container isCentered={half && !withImage}>
          <Text size={isDownSm ? 'caption' : 'body2'} isParagraph={false}>
            {participation}
          </Text>
        </S.Container>
      )}

      {isParticipating === undefined &&
        currentPhase === CHALLENGE_PHASE.DURING_SIGN_IN &&
        hasResultLimit && (
          <S.Container isCentered={half && !withImage}>
            <Fade in>
              <S.Title
                color="primary"
                size={isDownSm ? 'h5' : 'h4'}
                isCentered={half && !withImage}
              >
                <S.SeatIcon />
                {/* No more available places  */}
                <strong>
                  {t(
                    Number(inscriptionsLeft) < 2
                      ? inscriptionsLeft === 1
                        ? 'challenges.no_more_than_nb_available_places'
                        : 'challenges.noMoreAvailableSpaces'
                      : 'challenges.no_more_than_nb_available_places_plural',
                    { nb: inscriptionsLeft?.toLocaleString() },
                  )}
                </strong>
              </S.Title>
            </Fade>
          </S.Container>
        )}
    </React.Fragment>
  );
};

export default ActionParticipation;
